import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { getFirstAllowedRoute } from '../lib/nav-routes';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { MainLayout } from '../components/layout/MainLayout';
import { LoginPage } from '../app/auth/login/LoginPage';
import { RegisterPage } from '../app/auth/register/RegisterPage';
import { ForgotPasswordPage } from '../app/auth/forgot-password/ForgotPasswordPage';
import { UnauthorizedPage } from '../app/auth/unauthorized/UnauthorizedPage';
import { UsersPage } from '../app/admin/settings/users/UsersPage';
import { RolesPage } from '../app/admin/settings/roles/RolesPage';
import { RoleFormPage } from '../app/admin/settings/roles/[id]/RoleFormPage';
import { PermissionsPage } from '../app/admin/settings/permissions/PermissionsPage';
import { PaymentAccountsPage } from '../app/admin/settings/payment-accounts/PaymentAccountsPage';
import { PaymentAccountFormPage } from '../app/admin/settings/payment-accounts/[id]/PaymentAccountFormPage';
import { SystemConfigPage } from '../app/admin/settings/system/SystemConfigPage';
import { AuditLogsPage } from '../app/admin/settings/audit-logs/AuditLogsPage';
import { ProjectConfigPage } from '../app/admin/settings/project-config/ProjectConfigPage';
import { ServiceAccountsPage } from '../app/admin/settings/service-accounts/ServiceAccountsPage';
import { DesktopReleasesPage } from '../app/admin/settings/desktop-releases/DesktopReleasesPage';
import { PluginsPage } from '../app/admin/settings/desktop-plugins/PluginsPage';
import { DashboardRouter } from '../app/admin/dashboard/DashboardRouter';
import { ProductsPage } from '../app/admin/products/ProductsPage';
import { TenantsPage } from '../app/admin/tenants/TenantsPage';
import { SubscriptionsPage } from '../app/admin/subscriptions/SubscriptionsPage';
import { TenantUsersPage as AdminTenantUsersPage } from '../app/admin/tenants/tenant-users/TenantUsersPage';
import { PlansPage } from '../app/admin/plans/PlansPage';
import { ProductDemoPage } from '../app/admin/licensing/ProductDemoPage';
import { ProjectsPage } from '../app/admin/projects/ProjectsPage';
import { ProjectTimelinePage } from '../app/admin/projects/ProjectTimelinePage';
import { TenantAreaUsersPage } from '../app/tenant/users/TenantAreaUsersPage';
import { TenantAreaMyProductPage } from '../app/tenant/my-product/TenantAreaMyProductPage';
import { TenantAreaProjectsPage } from '../app/tenant/projects/TenantAreaProjectsPage';
import { TenantAreaProjectTimelinePage } from '../app/tenant/projects/TenantAreaProjectTimelinePage';
import { withAreaPrefix } from '../lib/area-routes';
import { AuthHydrationFallback } from '../components/auth/AuthHydrationFallback';
import { ProfilePage } from '../app/profile/ProfilePage';
// RMT legacy screens (crawl/proxies) removed from router.

/** Redirects / or unknown path to first allowed route when authenticated, else to login. */
function DefaultRedirect() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const hasScope = useAuthStore(state => state.hasScope);
  const user = useAuthStore(state => state.user);
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to={withAreaPrefix('/auth/login', location.pathname)} replace />;
  }
  if (user == null) {
    return <AuthHydrationFallback />;
  }
  const target = getFirstAllowedRoute(hasScope, location.pathname, user.isPlatformUser);
  return <Navigate to={target} replace />;
}

function AreaAdminRedirect(props: { readonly adminPath: string }) {
  const { adminPath } = props;
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const user = useAuthStore(state => state.user);
  const hasScope = useAuthStore(state => state.hasScope);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  if (user == null) {
    return <AuthHydrationFallback />;
  }

  if (user.isPlatformUser) {
    return <Navigate to={adminPath} replace />;
  }

  return <Navigate to={getFirstAllowedRoute(hasScope, location.pathname, false)} replace />;
}

function AreaAdminDemoRedirect() {
  const { productCode } = useParams<{ productCode: string }>();
  const adminPath = productCode ? `/admin/demo/${productCode}` : '/admin/products';
  return <AreaAdminRedirect adminPath={adminPath} />;
}

function AreaDashboardRedirect() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const user = useAuthStore(state => state.user);

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  if (user == null) {
    return <AuthHydrationFallback />;
  }

  if (user.isPlatformUser) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <ProtectedRoute>
      <MainLayout>
        <DashboardRouter />
      </MainLayout>
    </ProtectedRoute>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Auth routes (no layout) */}
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/register" element={<RegisterPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected routes (with layout) */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute requiredScope="dashboard:read">
            <MainLayout>
              <DashboardRouter />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={<AreaDashboardRedirect />}
      />
      <Route
        path="/admin/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProfilePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProfilePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Tenant self-service (non–platform users; paths are not under /admin) */}
      <Route
        path="/tenant/users"
        element={
          <ProtectedRoute requiredScope="tenant-user:read">
            <MainLayout>
              <TenantAreaUsersPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/my-product"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TenantAreaMyProductPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/projects"
        element={
          <ProtectedRoute requiredScope="project:read">
            <MainLayout>
              <TenantAreaProjectsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/projects/timeline/:projectId"
        element={
          <ProtectedRoute requiredScope="project:read">
            <MainLayout>
              <TenantAreaProjectTimelinePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin routes (products, plans, tenants, subscriptions, tenant-users, demo) */}
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute requiredScope="product:read">
            <MainLayout>
              <ProductsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin/products/new" element={<Navigate to="/admin/products" replace />} />
      <Route path="/admin/products/:id" element={<Navigate to="/admin/products" replace />} />
      <Route
        path="/products"
        element={<AreaAdminRedirect adminPath="/admin/products" />}
      />
      <Route path="/products/new" element={<AreaAdminRedirect adminPath="/admin/products" />} />
      <Route path="/products/:id" element={<AreaAdminRedirect adminPath="/admin/products" />} />
      <Route
        path="/admin/plans"
        element={
          <ProtectedRoute requiredScope="plan:read">
            <MainLayout>
              <PlansPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects"
        element={
          <ProtectedRoute requiredScope="project:read">
            <MainLayout>
              <ProjectsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/timeline/:projectId"
        element={
          <ProtectedRoute requiredScope="project:read">
            <MainLayout>
              <ProjectTimelinePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin/plans/:id" element={<Navigate to="/admin/plans" replace />} />
      <Route
        path="/plans"
        element={<AreaAdminRedirect adminPath="/admin/plans" />}
      />
      <Route
        path="/projects"
        element={<AreaAdminRedirect adminPath="/admin/projects" />}
      />
      <Route path="/plans/:id" element={<AreaAdminRedirect adminPath="/admin/plans" />} />
      <Route
        path="/admin/tenants"
        element={
          <ProtectedRoute requiredScope="tenant:manage">
            <MainLayout>
              <TenantsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin/tenants/new" element={<Navigate to="/admin/tenants" replace />} />
      <Route path="/admin/tenants/:id" element={<Navigate to="/admin/tenants" replace />} />
      <Route
        path="/tenants"
        element={<AreaAdminRedirect adminPath="/admin/tenants" />}
      />
      <Route path="/tenants/new" element={<AreaAdminRedirect adminPath="/admin/tenants" />} />
      <Route path="/tenants/:id" element={<AreaAdminRedirect adminPath="/admin/tenants" />} />
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute requiredScope="subscription:read">
            <MainLayout>
              <SubscriptionsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin/subscriptions/:id" element={<Navigate to="/admin/subscriptions" replace />} />
      <Route
        path="/admin/tenant-users"
        element={
          <ProtectedRoute requiredScope="tenant-user:read">
            <MainLayout>
              <AdminTenantUsersPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/demo/:productCode"
        element={
          <ProtectedRoute requiredScope="product:use">
            <MainLayout>
              <ProductDemoPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={<AreaAdminRedirect adminPath="/admin/subscriptions" />}
      />
      <Route path="/subscriptions/:id" element={<AreaAdminRedirect adminPath="/admin/subscriptions" />} />
      <Route
        path="/tenant-users"
        element={<AreaAdminRedirect adminPath="/admin/tenant-users" />}
      />
      <Route
        path="/demo/:productCode"
        element={<AreaAdminDemoRedirect />}
      />

      {/* Redirect legacy /saas/* to /* */}
      <Route path="/saas/products" element={<AreaAdminRedirect adminPath="/admin/products" />} />
      <Route path="/saas/products/*" element={<AreaAdminRedirect adminPath="/admin/products" />} />
      <Route path="/saas/plans" element={<AreaAdminRedirect adminPath="/admin/plans" />} />
      <Route path="/saas/plan-billing" element={<AreaAdminRedirect adminPath="/admin/plans" />} />
      <Route path="/saas/plan-billing/*" element={<AreaAdminRedirect adminPath="/admin/plans" />} />
      <Route path="/saas/tenants" element={<AreaAdminRedirect adminPath="/admin/tenants" />} />
      <Route path="/saas/tenants/*" element={<AreaAdminRedirect adminPath="/admin/tenants" />} />
      <Route path="/saas/subscriptions" element={<AreaAdminRedirect adminPath="/admin/subscriptions" />} />
      <Route path="/saas/subscriptions/*" element={<AreaAdminRedirect adminPath="/admin/subscriptions" />} />
      <Route path="/saas/tenant-users" element={<AreaAdminRedirect adminPath="/admin/tenant-users" />} />
      <Route path="/saas/demo/:productCode" element={<AreaAdminDemoRedirect />} />

      {/* Settings routes */}
      <Route
        path="/admin/settings/users"
        element={
          <ProtectedRoute requiredScope="user:read">
            <MainLayout>
              <UsersPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin/settings/users/new" element={<Navigate to="/admin/settings/users" replace />} />
      <Route path="/admin/settings/users/:id" element={<Navigate to="/admin/settings/users" replace />} />
      <Route
        path="/admin/settings/roles"
        element={
          <ProtectedRoute requiredScope="role:read">
            <MainLayout>
              <RolesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/roles/:id"
        element={
          <ProtectedRoute requiredScope="role:update">
            <MainLayout>
              <RoleFormPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/permissions"
        element={
          <ProtectedRoute requiredScope="role:read">
            <MainLayout>
              <PermissionsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/payment-accounts"
        element={
          <ProtectedRoute requiredScope="payment-account:read">
            <MainLayout>
              <PaymentAccountsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/payment-accounts/new"
        element={
          <ProtectedRoute requiredScope="payment-account:create">
            <MainLayout>
              <PaymentAccountFormPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/payment-accounts/:id"
        element={
          <ProtectedRoute requiredScope="payment-account:update">
            <MainLayout>
              <PaymentAccountFormPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/project-config"
        element={
          <ProtectedRoute requiredScope="project:read">
            <MainLayout>
              <ProjectConfigPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/service-accounts"
        element={
          <ProtectedRoute requiredScope="service-account:read">
            <MainLayout>
              <ServiceAccountsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/desktop-releases"
        element={
          <ProtectedRoute requiredScope="desktop_release:read">
            <MainLayout>
              <DesktopReleasesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/desktop-plugins"
        element={
          <ProtectedRoute requiredScope="desktop_plugin:read">
            <MainLayout>
              <PluginsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/system"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SystemConfigPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/audit-logs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AuditLogsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/users"
        element={<AreaAdminRedirect adminPath="/admin/settings/users" />}
      />
      <Route path="/settings/users/new" element={<AreaAdminRedirect adminPath="/admin/settings/users" />} />
      <Route path="/settings/users/:id" element={<AreaAdminRedirect adminPath="/admin/settings/users" />} />
      <Route
        path="/settings/roles"
        element={<AreaAdminRedirect adminPath="/admin/settings/roles" />}
      />
      <Route
        path="/settings/roles/:id"
        element={<AreaAdminRedirect adminPath="/admin/settings/roles" />}
      />
      <Route
        path="/settings/permissions"
        element={<AreaAdminRedirect adminPath="/admin/settings/permissions" />}
      />
      <Route
        path="/settings/payment-accounts"
        element={<AreaAdminRedirect adminPath="/admin/settings/payment-accounts" />}
      />
      <Route
        path="/settings/payment-accounts/new"
        element={<AreaAdminRedirect adminPath="/admin/settings/payment-accounts" />}
      />
      <Route
        path="/settings/payment-accounts/:id"
        element={<AreaAdminRedirect adminPath="/admin/settings/payment-accounts" />}
      />
      <Route
        path="/settings/project-config"
        element={<AreaAdminRedirect adminPath="/admin/settings/project-config" />}
      />
      <Route
        path="/settings/service-accounts"
        element={<AreaAdminRedirect adminPath="/admin/settings/service-accounts" />}
      />
      <Route
        path="/settings/desktop-releases"
        element={<AreaAdminRedirect adminPath="/admin/settings/desktop-releases" />}
      />
      <Route
        path="/settings/desktop-plugins"
        element={<AreaAdminRedirect adminPath="/admin/settings/desktop-plugins" />}
      />
      <Route
        path="/settings/system"
        element={<AreaAdminRedirect adminPath="/admin/settings/system" />}
      />
      <Route
        path="/settings/audit-logs"
        element={<AreaAdminRedirect adminPath="/admin/settings/audit-logs" />}
      />
      {/* RMT legacy: crawl jobs + proxies screens removed */}

      {/* FR001 Phase 0: routes above for RMT domain (products, orders, inventory, prices, crawl, suppliers, customers, reports, financial) are temporarily disabled. */}

      {/* Default redirect: first allowed route when authenticated, else login */}
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}
